import type { Lot, Position } from '@/types/api/accounts'

import { BondType } from '@moment/trading-types/reference-data'
import { Decimal } from 'decimal.js-light'

import { getPrincipal } from '@/utils/order-calculations'

type DecimalPosition = Omit<
  Position & { sleeve_names?: string[] },
  | 'total_accrued_interest'
  | 'open_quantity'
  | 'sellable_quantity'
  | 'avg_cost_basis'
  | 'total_basis_quantity'
  | 'market_value'
  | 'cost_basis_total'
  | 'weight'
  | 'gain_loss'
  | 'book_ytm'
  | 'book_ytc'
  | 'book_ytw'
> & {
  total_accrued_interest: Decimal
  open_quantity: Decimal
  sellable_quantity: Decimal
  avg_cost_basis: Decimal
  total_basis_quantity: Decimal
  market_value: Decimal
  cost_basis_total: Decimal
  weight: Decimal
  gain_loss: Decimal
  book_ytm_weighted: Decimal
  book_ytc_weighted: Decimal
  book_ytw_weighted: Decimal
  total_market_value_for_book_ytm: Decimal
  total_market_value_for_book_ytc: Decimal
  total_market_value_for_book_ytw: Decimal
}

export function aggregatePosition(
  lots: Lot[],
  totalAssets: number,
  isSleeveSelected: boolean,
  sleeveIdToName?: Record<string, string>,
): Array<Position & { sleeve_names?: string[] }> {
  // Group lots by ISIN, accumulating values
  const positionsByIsin = lots.reduce<Record<string, DecimalPosition>>(
    (acc, lot) => {
      const isin = lot.instrument.isin
      const existing = acc[isin]
      const { instrument } = lot

      // HOTFIX: Fallback to close price if price is not available
      const price = instrument.price ?? instrument.close_price

      // Create position if it doesn't exist
      if (!existing) {
        acc[isin] = {
          id: isin,
          isin,
          par_value: instrument.par_value,
          sleeve_ids: [],
          sleeve_names: [],
          description: instrument.description,
          issuer: instrument.issuer,
          coupon: instrument.coupon,
          maturity_date: instrument.maturity_date,
          sp_rating: instrument.sp_rating,
          moodys_rating: instrument.moodys_rating,
          current_price: price,
          yield_to_worst: instrument.yield_to_worst,
          sector: instrument.sector,
          cusip: instrument.cusip,
          type: instrument.type,
          total_accrued_interest: new Decimal(0),
          open_quantity: new Decimal(0),
          sellable_quantity: new Decimal(0),
          avg_cost_basis: new Decimal(0),
          total_basis_quantity: new Decimal(0),
          market_value: new Decimal(0),
          cost_basis_total: new Decimal(0),
          weight: new Decimal(0),
          gain_loss: new Decimal(0),
          book_ytm_weighted: new Decimal(0),
          book_ytc_weighted: new Decimal(0),
          book_ytw_weighted: new Decimal(0),
          total_market_value_for_book_ytm: new Decimal(0),
          total_market_value_for_book_ytc: new Decimal(0),
          total_market_value_for_book_ytw: new Decimal(0),
        }
      }

      const position = acc[isin]

      // Track sleeves
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      if (!position!.sleeve_ids.includes(lot.sleeve_id)) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.sleeve_ids.push(lot.sleeve_id)
      }

      // Convert values to Decimal
      const lotQuantity = new Decimal(lot.open_quantity ?? 0)
      const settledQuantity = new Decimal(lot.settled_open_quantity ?? 0)
      const basisQuantity = new Decimal(lot.basis_quantity ?? 0)
      const principalFactor = new Decimal(instrument.principal_factor ?? 1)

      // Accumulate values
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.open_quantity = position!.open_quantity.add(lotQuantity)
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.sellable_quantity =
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.sellable_quantity.add(settledQuantity)
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.total_accrued_interest = position!.total_accrued_interest.add(
        lot.accrued_interest ?? 0,
      )
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.avg_cost_basis = position!.avg_cost_basis.add(
        new Decimal(lot.basis_price).mul(basisQuantity),
      )
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.total_basis_quantity =
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.total_basis_quantity.add(basisQuantity)
      const lotMarketValue = getPrincipal({
        // HOTFIX: Fallback to close price if price is not available
        price: new Decimal(price ?? 0),
        quantity: lotQuantity,
        principalFactor,
      })

      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.market_value = position!.market_value.add(lotMarketValue)
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      position!.cost_basis_total = position!.cost_basis_total.add(
        getPrincipal({
          price: new Decimal(lot.basis_price),
          quantity: lotQuantity,
          principalFactor,
        }),
      )

      // Accumulate weighted book yields if available
      if (lot.book_ytm != null) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.book_ytm_weighted = position!.book_ytm_weighted.add(
          new Decimal(lot.book_ytm).mul(lotMarketValue),
        )
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.total_market_value_for_book_ytm =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          position!.total_market_value_for_book_ytm.add(lotMarketValue)
      }

      if (lot.book_ytc != null) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.book_ytc_weighted = position!.book_ytc_weighted.add(
          new Decimal(lot.book_ytc).mul(lotMarketValue),
        )
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.total_market_value_for_book_ytc =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          position!.total_market_value_for_book_ytc.add(lotMarketValue)
      }

      if (lot.book_ytw != null) {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.book_ytw_weighted = position!.book_ytw_weighted.add(
          new Decimal(lot.book_ytw).mul(lotMarketValue),
        )
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        position!.total_market_value_for_book_ytw =
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          position!.total_market_value_for_book_ytw.add(lotMarketValue)
      }

      return acc
    },
    {},
  )

  const positions = Object.values(positionsByIsin)

  // Calculate total market value for weight calculations
  const totalMarketValue = positions.reduce(
    (sum, pos) => sum.add(pos.market_value),
    new Decimal(0),
  )

  // Finalize positions: calculate derived values and convert to numbers
  return positions.map((pos) => {
    // Calculate sellable quantity
    // For MoneyMarket, Treasury, and zero par value: use the raw sellable quantity
    // (these instrument types can have fractional positions)
    // For other bond types: round down to the nearest par value increment
    const sellableQuantity =
      pos.type === BondType.MoneyMarket ||
      pos.type === BondType.Treasury ||
      pos.par_value === 0
        ? pos.sellable_quantity
        : pos.sellable_quantity.sub(pos.sellable_quantity.mod(pos.par_value))

    // Calculate average cost basis
    const avgCostBasis = pos.total_basis_quantity.eq(0)
      ? new Decimal(0)
      : pos.avg_cost_basis.div(pos.total_basis_quantity)

    // Calculate weight as percentage
    const weightDenominator = isSleeveSelected
      ? totalMarketValue
      : new Decimal(totalAssets)
    const weight = weightDenominator.eq(0)
      ? new Decimal(0)
      : pos.market_value.div(weightDenominator).mul(100)

    // Map sleeve IDs to names
    const sleeveNames = sleeveIdToName
      ? pos.sleeve_ids.map((id) => sleeveIdToName[id] || id)
      : []

    // Calculate weighted average book yields
    const bookYtm = pos.total_market_value_for_book_ytm.eq(0)
      ? null
      : pos.book_ytm_weighted
          .div(pos.total_market_value_for_book_ytm)
          .toNumber()

    const bookYtc = pos.total_market_value_for_book_ytc.eq(0)
      ? null
      : pos.book_ytc_weighted
          .div(pos.total_market_value_for_book_ytc)
          .toNumber()

    const bookYtw = pos.total_market_value_for_book_ytw.eq(0)
      ? null
      : pos.book_ytw_weighted
          .div(pos.total_market_value_for_book_ytw)
          .toNumber()

    // Destructure out internal Decimal fields before returning
    const {
      book_ytm_weighted: _ytm,
      book_ytc_weighted: _ytc,
      book_ytw_weighted: _ytw,
      total_market_value_for_book_ytm: _totalMvYtm,
      total_market_value_for_book_ytc: _totalMvYtc,
      total_market_value_for_book_ytw: _totalMvYtw,
      ...positionWithoutInternals
    } = pos

    return {
      ...positionWithoutInternals,
      sleeve_names: sleeveNames,
      total_accrued_interest: pos.total_accrued_interest.toNumber(),
      open_quantity: pos.open_quantity.toNumber(),
      sellable_quantity: sellableQuantity.toNumber(),
      avg_cost_basis: avgCostBasis.toNumber(),
      total_basis_quantity: pos.total_basis_quantity.toNumber(),
      market_value: pos.market_value.toDecimalPlaces(2).toNumber(),
      cost_basis_total: pos.cost_basis_total.toNumber(),
      weight: weight.toNumber(),
      gain_loss: pos.market_value
        .sub(pos.cost_basis_total)
        .toDecimalPlaces(2)
        .toNumber(),
      book_ytm: bookYtm,
      book_ytc: bookYtc,
      book_ytw: bookYtw,
    }
  })
}
